import React from 'react'
import './article.css'
import bannerImage2 from './assets/Advanced_Test_Reactor.jpg'
import Header from './Header'
import TypesPie from "./ReactorTypesCanvas/TypesPie.js"
import Temp from "./ReactorTypesCanvas/Temp.js"

const Post2 = () => {
  return (
    <div>
      <Header title="Types of Reactors" image={bannerImage2}/>
      <article>
        <header>
          <p>By <strong>Milo Peterson</strong> | <time dateTime="2025-06-2">July 20, 2025</time></p>
        </header>

        <section>
          <h2>Why Type Matters</h2>
          <p>
            Most of the “differentiation” among the types of nuclear reactors involves simple trade-offs—like fuel efficiency
            for a more costly moderator, safety for simplicity, or complexity for efficiency. Not all designs are without merit,
            and I’ll go over a couple. More recently, though, there have been some wildly different designs that could be incredibly
            useful—or completely impractical and a waste of effort. Either way, I’m going to explore a few of the more interesting ones.
          </p>
        </section>

        <section>
          <h2>PWR’s Dominance</h2>
          <p>
            First, I’m going to quickly cover the most widely used, oldest, and simplest reactor type. Pressurized Water Reactors (PWRs)
            make up by far the largest share of the world’s nuclear energy. In fact, here’s a chart showing that dominance.
          </p>
          <TypesPie/>
          <p>
            The widespread use of PWRs isn’t due to their cost. In fact, PWRs are comparatively difficult to make and not particularly
            cost-effective. This is because of both historical reasons and safety concerns. They were used on U.S. Navy aircraft carriers
            in the 1950s and 1960s, which meant they were well tested and already had some supporting infrastructure.
            More importantly, PWRs were designed in such a way that they weren’t considered a proliferation risk.
            They were difficult enough to build that only a few countries with the right infrastructure could manage it,
            and the waste they produced was exceedingly hard to use for bomb-making.
          </p>
          <p>
            PWRs aren’t really worth going into too much depth on here. They’re by far the most widespread and, in many ways,
            the least interesting reactor type.
          </p>
          <p>
            There are alternatives worth talking about, though. Next, I’m going to cover what I think is the most interesting new reactor type.
            The goal of all other reactor designs should be to be just enough better than PWRs to serve as a viable replacement.
          </p>
        </section>

        <section>
          <h2>Molten Salt Reactors</h2>
          <p>
            Oak Ridge National Laboratory (ORNL), a leader in MSR technology, strongly advocates for their commercialization
            and widespread use. First, the advantages: fluoride or chloride salts as coolants allow for far higher energy efficiency
            than other types of reactors.
          </p>
          <Temp/>
          <p>
            The maximum operating temperature has a drastic effect on how efficient a reactor can be. Being able to operate
            at higher temperatures makes reactors more efficient. Improving maximum temperature is, therefore, a key priority
            when designing the next generation of reactors.
          </p>
          <p>
            Molten Salt Reactors are also designed to use less fuel and produce shorter-lived radioactive waste. It should be noted though, that 
            the decreased waste depends on the specific design and surrounding infrastructure of the reactor. This has the potential
            to significantly improve the overall safety of these reactors. Economically, the improved fuel process would also serve
            as a massive cost reducer in the long run through fewer refueling outages and less waste.
          </p>
          <p>
            MSRs suffer from a lack of testing and commercial precedent. Oak Ridge National Lab has its own test reactor and is working
            toward commercial viability, but there’s still a distinct lack of commercial reactors in development. MSRs could play a major
            role in next-gen nuclear energy. They’ve been proven to work more efficiently and safely than current models, and soon
            we might see the first commercial MSR.
          </p>
        </section>

        <section>
          <h2>Sodium-Cooled Fast Reactor</h2>
          <p>
            Sodium-cooled reactors are one type of Liquid Metal Reactor (LMR); the others typically use lead or lead-bismuth as the coolant.
            Using sodium instead of water allows for much higher operating temperatures and lower operating pressure.
            Sodium reactors are therefore generally both safer and more efficient than typical pressurized water reactors.
          </p>
          <p>
            Additionally, neutrons aren’t slowed down like they are in water-cooled reactors. Because of this, neutrons in an SFR move
            much faster than in a PWR. This means SFRs can not only use common fuels like U-235, but they can also make use of materials
            that normally accumulate as waste in other reactors—such as U-238 (which can be converted into Pu-239) and plutonium.
          </p>
          <p>
             Now this does raise an interesting question. Why haven't we been using these designs for decades? The truth is we've had the ability to make these types of 
             reactors for dozens of years, but we've had to shelve them for a number of reasons. First, nuclear weapon proliferation has and always will be a major concer, and 
             these reactors could be built much smaller than a traditional PWR. PWRs had the advantage and disandvantage of being incredibly large and complex construction projects, 
             a smaller scale project, like for a LMR, could make it easier to make reactors, and in turn, make the processed uranium necessary for nuclear weapons.

          </p>
          <p>
            Another reason for the lack of Next-Gen reactors is their lack of testing. The first reactor would be prohibitvely expensive, delays would be all but guaranteed, 
            and who knows what other roadblocks you might face in contruction. In an industry already rife with, risk its easy to understand why investing in something that doubles 
            or triples your already high risk. Most of the time its just not worth it.
          </p>
        </section>

        <section>
          <h2>The Future of Next-Gen Reactors</h2>
          <p>
            So are we screwed? Are these reactors just to risky to build. Almost definitely not.
          </p>
          <p>
            While there is risk, there is also great reward. The tenth next-gen reactor build will be a third the cost of the first, and these new reactors will be much more efficient. 
            There are companies like Thorcon that are already pushing for MSRs to hit commercial markets. Its not easy, and in the past - especially with reactors like Hinkley - using new designs is difficult. 
            The reward will be much cheaper, and much cleaner energy which is something I believe can't be overlooked.
          </p>
        </section>

<section>
  <h2>References</h2>

  “Main Types of Nuclear Reactor.” <em>World Nuclear Association</em>, &nbsp;
  <a href="https://world-nuclear.org/information-library/nuclear-fuel-cycle/nuclear-power-reactors/nuclear-power-reactors" target="_blank" rel="noreferrer">
    world-nuclear.org/information-library/nuclear-fuel-cycle/nuclear-power-reactors/nuclear-power-reactors
  </a>. 
  Accessed 11 Oct. 2025.
  <br /><br />

  “Molten Salt Reactor.” <em>Oak Ridge National Laboratory (ORNL)</em>, &nbsp;
  <a href="https://www.ornl.gov/molten-salt-reactor" target="_blank" rel="noreferrer">
    www.ornl.gov/molten-salt-reactor
  </a>. 
  Accessed 11 Oct. 2025.
  <br /><br />

  “3 Advanced Reactor Systems to Watch by 2030.” <em>Office of Nuclear Energy</em>, 
  U.S. Department of Energy, 12 Apr. 2021, &nbsp;
  <a href="https://www.energy.gov/ne/articles/3-advanced-reactor-systems-watch-2030" target="_blank" rel="noreferrer">
    www.energy.gov/ne/articles/3-advanced-reactor-systems-watch-2030
  </a>. 
  Accessed 11 Oct. 2025.
</section>


        <footer>
          
        </footer>
      </article>
    </div>
  )
}

export default Post2
